import './DynamicTable.css';

import { Button, ButtonGroup, Col, Form, FormControl, InputGroup, Row, Table } from 'react-bootstrap';
import {
  useAsyncDebounce,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
  useResizeColumns,
  useBlockLayout,
} from 'react-table';
import React from 'react';
import Roller from 'react-spinners-css/dist/Roller';

/*
 * This is an internal component that represents the search bar.
 */
const GlobalFilter = ({ preGlobalFilteredRows, globalFilter, setGlobalFilter }) => {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <InputGroup size="sm">
      <InputGroup.Prepend>
        <InputGroup.Text>Search: </InputGroup.Text>
      </InputGroup.Prepend>
      <Form.Control
        value={value || ''}
        onChange={e => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={`${count} record${count === 1 ? '' : 's'}...`}
      />
    </InputGroup>
  );
};

/*
 * Dynamic table component. Supports filtering of elements, and pagination.
 * Uses: https://react-table.tanstack.com/
 * Pagination is done locally, meaning that all elements have been already fetched from the backend.
 *
 * There is a loading spinner that is represented by two boolean flags:
 *   inProgress and showTableDataWhileInProgress
 * inProgress controls whether spinner is shown or not.
 * showTableDataWhileInProgress controls whether spinner will be in the center of the table container or in place of table data.
 *
 * The whole table is wrapped in a div, and 'additionalStyles' prop takes in classes to add to that div.
 * The wrapper div has width: 100% on it by default. Also, classes are added to prevent user interactions for 'inProgress'.
 *
 * The table columns will take a 'className' prop from column definition and apply it directly to header.
 * Do not use sizing classes as it interfers with the column width resizer.
 * {
 *   Header: 'x',
 *   accessor: 'y',
 *   className: 'bg-primary',
 * }
 *
 * Another notable column prop is 'disableSortBy', which will disable the sorting on the column.
 *
 * REMEMBER:
 *   Column definitions MUST be memoized inside of the component that uses the table
 *   (https://react-table.tanstack.com/docs/api/useTable):
 * const AppComponent = () => {
 *   const columns = useMemo(
 *     () => [
 *       {
 *         Header: 'x',
 *         accessor: 'y'
 *       }
 *     ],
 *   []);
 *   <DynamicTable
 *     columns={columns}
 *     ...
 *   />
 * }
 */
export const DynamicTable = ({
  columns,
  data,
  initialState = {},
  defaultColumn = {},
  inProgress = false,
  showTableDataWhileInProgress = true,
  additionalStyles = '',
  getRowId, // allows to define rowId is constructed
}) => {
  const tableInstance = useTable(
    {
      columns: columns,
      data: data,
      getRowId: getRowId,
      initialState: initialState,
      defaultColumn: defaultColumn,
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useBlockLayout,
    useResizeColumns
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    preGlobalFilteredRows,
    setGlobalFilter,
  } = tableInstance;

  return (
    <div className={'w-100 ' + additionalStyles + (inProgress ? ' no-user-interactions half-opaque ' : '')}>
      <Row className="mb-2">
        <Col className="col-3">
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </Col>
      </Row>

      <Table {...getTableProps()} striped bordered hover responsive className="table-condensed">
        <thead>
          {
            // Loop over the header rows
            headerGroups.map(headerGroup => (
              // Apply the header row props
              <tr {...headerGroup.getHeaderGroupProps()}>
                {
                  // Loop over the headers in each row
                  headerGroup.headers.map(column => (
                    // Apply the header cell props
                    <th {...column.getHeaderProps({ className: column.className })}>
                      <div {...column.getSortByToggleProps()}>
                        {
                          // Render the header
                          column.render('Header')
                        }
                        {
                          // Sorting - https://react-table.tanstack.com/docs/examples/sorting
                          <span>{column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}</span>
                        }
                      </div>
                      {column.disableResizing ? null : (
                        // Resizing - https://react-table.tanstack.com/docs/examples/column-resizing
                        <div {...column.getResizerProps()} className="resizer" />
                      )}
                    </th>
                  ))
                }
              </tr>
            ))
          }
        </thead>
        <tbody {...getTableBodyProps()}>
          {(!inProgress || (inProgress && showTableDataWhileInProgress)) &&
            // Loop over the table rows
            page.map((row, i) => {
              // Prepare the row for display
              prepareRow(row);
              return (
                // Apply the row props
                <tr {...row.getRowProps()}>
                  {
                    // Loop over the rows cells
                    row.cells.map(cell => {
                      // Apply the cell props
                      return (
                        <td {...cell.getCellProps({ className: cell.column.className })}>
                          {
                            // Render the cell contents
                            cell.render('Cell')
                          }
                        </td>
                      );
                    })
                  }
                </tr>
              );
            })}
        </tbody>
      </Table>

      <Row
        hidden={!inProgress}
        className={
          showTableDataWhileInProgress
            ? 'position-absolute l-50 t-50 transl-x-plus-50'
            : 'align-items-center justify-content-center'
        }
      >
        <Col xs={3} sm={3} md={2} lg={1}>
          <Roller color="grey" />
        </Col>
      </Row>

      <div>
        <Row className="justify-content-between">
          <Col xs={4} sm={4} md={3} lg={2} xl="auto">
            <Row>
              <Col className="mb-2">
                <ButtonGroup>
                  <Button variant="outline-secondary" size="sm" disabled={!canPreviousPage} onClick={e => gotoPage(0)}>
                    first
                  </Button>{' '}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!canPreviousPage}
                    onClick={e => previousPage()}
                  >
                    {pageIndex}
                  </Button>{' '}
                  <Button variant="outline-secondary" size="sm" disabled={!canNextPage} onClick={e => nextPage()}>
                    {pageIndex + 2}
                  </Button>{' '}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!canNextPage}
                    onClick={e => gotoPage(pageCount - 1)}
                  >
                    last
                  </Button>
                </ButtonGroup>
              </Col>
              <Col>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text>Page: </InputGroup.Text>
                  </InputGroup.Prepend>
                  <FormControl
                    bsPrefix="width-65px form-control"
                    htmlSize={5}
                    type="number"
                    disabled={pageCount < 2}
                    defaultValue={pageIndex + 1}
                    onChange={e => {
                      let pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                      if (pageNumber >= pageCount) {
                        pageNumber = pageCount - 1;
                      }
                      gotoPage(pageNumber);
                    }}
                  />
                </InputGroup>
              </Col>
            </Row>
          </Col>

          <Col xs="auto" sm="auto" md="auto" lg="auto" xl="auto">
            <label>
              Page <b>{pageIndex + 1}</b> of <b>{pageCount}</b>
            </label>{' '}
          </Col>

          <Col xs={4} sm="auto" md="auto" lg="auto" xl="auto">
            <InputGroup size="sm">
              <InputGroup.Prepend>
                <InputGroup.Text>Page size: </InputGroup.Text>
              </InputGroup.Prepend>
              <FormControl
                bsPrefix="form-control width-65px"
                as="select"
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
              >
                {[10, 30, 50].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </FormControl>
            </InputGroup>
          </Col>
        </Row>
      </div>
    </div>
  );
};
